import { CaretRightIcon } from '@radix-ui/react-icons';
import { Badge, Flex, Heading, IconButton, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { TrainingOutcome } from 'lib_ts/enums/training.enums';
import {
  ITrainingLog,
  ITrainingLogExt,
} from 'lib_ts/interfaces/training/i-training-log';
import { sum } from 'lodash';
import { useMemo } from 'react';

interface StatusDef {
  label: string;
  sort: number;
  color: RadixColor;
}

const getStatus = (log: ITrainingLog): StatusDef => {
  const outcome = TrainingHelper.getLogOutcome(log);

  switch (outcome) {
    case TrainingOutcome.PT_GiveUp:
      return {
        label: TrainingOutcome.PT_GiveUp,
        sort: 200,
        color: RADIX.COLOR.WARNING,
      };
    case TrainingOutcome.PT_Complete:
      return {
        label: TrainingOutcome.PT_Complete,
        sort: 202,
        color: RADIX.COLOR.SUCCESS,
      };
    case TrainingOutcome.PT_Failure:
      return {
        label: TrainingOutcome.PT_Failure,
        sort: 201,
        color: RADIX.COLOR.DANGER,
      };
    case TrainingOutcome.QT_GiveUp:
      return {
        label: TrainingOutcome.QT_GiveUp,
        sort: 100,
        color: RADIX.COLOR.WARNING,
      };
    case TrainingOutcome.QT_Complete:
      return {
        label: TrainingOutcome.QT_Complete,
        sort: 102,
        color: RADIX.COLOR.INFO,
      };
  }
};

export const TrainingPerfLogsTable = () => {
  const loading = useTrainingPerformanceStore((s) => s.loading);
  const logs = useTrainingPerformanceStore((s) => s.logs);

  const columns = useMemo(() => {
    const getShots = (m: ITrainingLog) =>
      sum(m.iterations.map((itr) => itr.shotIDs.length));

    const getExcessShots = (m: ITrainingLog) =>
      Math.max(
        0,
        sum(m.iterations.map((itr) => itr.shotIDs.length)) -
          m.training_threshold
      );

    const output: ITypedTableColumn<ITrainingLogExt>[] = [
      {
        label: 'Pitch Name',
        key: 'pitchName',
      },
      {
        label: 'Pitch Type',
        key: 'pitchType',
        formatFn: (m) => m.lookupPitch?.type,
      },
      {
        label: 'Priority',
        key: 'pitchPriority',
        align: 'center',
      },
      {
        label: 'Tracking Device',
        key: 'tracking_device',
        align: 'center',
      },
      {
        label: 'Status',
        key: 'status',
        align: 'center',
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getStatus(m).sort);
          return aVal < bVal ? -dir : dir;
        },
        formatFn: (m) => {
          const status = getStatus(m);
          return <Badge color={status.color}>{status.label}</Badge>;
        },
      },
      {
        label: 'Iterations',
        key: 'iterations',
        align: 'center',
        formatFn: (m) => (
          <Flex gap="1" align="center" justify="center">
            <Text>{m.iterations.length}</Text>
            <IconButton
              size="1"
              className="btn-floating"
              disabled={m.iterations.length === 0}
              variant={RADIX.BUTTON.VARIANT.BORDERLESS}
              color={RADIX.COLOR.NEUTRAL}
              onClick={() =>
                useTrainingPerformanceStore.getState().setSelectedLog(m)
              }
            >
              <CaretRightIcon />
            </IconButton>
          </Flex>
        ),
        sortRowsFn: (a, b, dir) =>
          a.iterations.length < b.iterations.length ? -dir : dir,
      },
      {
        label: 'Shots',
        key: 'shots',
        align: 'center',
        formatFn: getShots,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) =>
            sum(m.iterations.map((itr) => itr.shotIDs.length))
          );
          return aVal < bVal ? -dir : dir;
        },
      },
      {
        label: 'Excess Shots',
        key: 'excessShots',
        align: 'center',
        formatFn: getExcessShots,
        sortRowsFn: (a, b, dir) => {
          const [aVal, bVal] = [a, b].map((m) => getExcessShots(m));
          return aVal < bVal ? -dir : dir;
        },
      },
    ];

    return output;
  }, []);

  return (
    <ErrorBoundary identifier="TrainingPerfLogsTable">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        <FlexTableWrapper
          gap="2"
          header={<Heading size="3">Precision Train Attempts</Heading>}
          table={
            <CommonTableHoC
              identifier="TrainingPerfLogsTable"
              dataTestID="TrainingPerfLogsTable"
              displayColumns={columns}
              displayData={logs}
              loading={loading}
              enableSort
              vFlex
              enableSelect
            />
          }
        />
      </Flex>
    </ErrorBoundary>
  );
};
